import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { QueueService, TaskWithActions, Action } from '@core/services/queue.service';
import { UrlService } from '@core/services/url.service';
import { LoggerService } from '@shared/services/logger.service';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { DatabaseService } from '@core/services/database.service';
import { CreateFileRequest } from '@core/interfaces/requests/create-file-request';
import { NativeFileService } from '@shared/services/native-file.service';
import { SyncCreateTaskFile } from '@core/interfaces/requests/sync-create-task-file';
import { AddHtmlReportRequest } from '@core/interfaces/requests/add-report-html';
import { ReportTemplateImages } from '@orm-entities/report-template-images';
import { UpdateReportRequest } from '@core/interfaces/requests/update-report-request';
import { convertToCorrosionMonitoringLocation } from '@core/interfaces/corrosion-monitoring-location';
import { Encoding } from '@capacitor/filesystem';
import { DeviceService } from '@core/services/device.service';
import { DEVICE_ID_HEADER } from '@shared/headers';
import { UpdateTaskFileRequest } from '@core/interfaces/requests/file/update-task-file.request';
import { SyncUpdateTaskFile } from '@core/interfaces/requests/sync-update-task-file';
import { TaskId, TasksStatusService } from './tasks-status.service';
import { DownloadedTasksService } from './downloaded-tasks.service';
import { SyncBackupService } from './sync-backup.service';

export type CompletedSyncResult = {
  status: 'CompletedWithErrors' | 'Completed';
  taskSyncResults: TaskSyncResults;
};
type SyncResult =
  | {
      status: 'CannotSync' | 'NoTasksToSync' | 'InProgress' | 'Completed';
    }
  | CompletedSyncResult;

export type CompletedLastSyncResult = {
  status: 'Completed' | 'CompletedWithErrors';
  fullySyncedTasks: { taskId: string; projectId: string }[];
};
export type LastSyncResult = { status: 'NoSyncYet' } | CompletedLastSyncResult;

export type SyncProgress = {
  [projectId: string]: {
    [taskId: string]: {
      totalItemsToSync: number;
      syncedItems: number;
    };
  };
};
type SyncTaskBundleError = Error;
type TaskToSync = TaskWithActions;
type BundledTaskToSync = TaskToSync & { bundleIndex: number };
export type TaskSyncResult = FullySyncedTask | PartiallySyncedTask | NotSyncedTask;
type FullySyncedTask = TaskToSync & { result: 'FullySynced' };
type PartiallySyncedTask = TaskToSync & { result: 'PartiallySynced'; syncedItemsCount: number; error: SyncTaskBundleError };
type NotSyncedTask = TaskToSync & { result: 'NotSynced'; error: SyncTaskBundleError };
export class TaskSyncResults {
  public constructor(public all: TaskSyncResult[]) {}

  public get partiallySyncedTasks(): PartiallySyncedTask[] {
    return this.all.filter(result => result.result === 'PartiallySynced');
  }

  public get fullySyncedTasks(): FullySyncedTask[] {
    return this.all.filter(result => result.result === 'FullySynced');
  }

  public get failedTasks(): (PartiallySyncedTask | NotSyncedTask)[] {
    return this.all.filter(result => result.result !== 'FullySynced');
  }

  public hasFailures(): boolean {
    return this.failedTasks.length > 0;
  }
}
type SyncActionPayload = {
  id: string;
  sequence: number;
  actionType: string;
  data: string;
  file?: File;
  version?: number;
  appVersion?: string;
  platform?: string;
};
@Injectable({
  providedIn: 'root',
})
export class SyncService {
  #queueService = inject(QueueService);
  #logger = inject(LoggerService);
  #httpClient = inject(HttpClient);
  #urlService = inject(UrlService);
  #nativeFileService = inject(NativeFileService);
  #databaseService = inject(DatabaseService);
  #tasksStatusService = inject(TasksStatusService);
  #downloadedTasksService = inject(DownloadedTasksService);
  #syncBackupService = inject(SyncBackupService);
  #deviceService = inject(DeviceService);

  public currentSyncProgress$: Observable<SyncProgress>;
  public currentOverallSyncProgress$: Observable<number>;
  public isCurrentlySyncing$: Observable<boolean>;
  public lastSyncResult$: Observable<LastSyncResult>;
  #lastSyncResult = new BehaviorSubject<LastSyncResult>({ status: 'NoSyncYet' });
  #currentSyncProgressSubject = new BehaviorSubject<SyncProgress>({});
  #bundleSize = 50;

  public constructor() {
    this.currentSyncProgress$ = this.#currentSyncProgressSubject.asObservable();
    this.currentOverallSyncProgress$ = this.currentSyncProgress$.pipe(
      map(projectsProgress => {
        const totalItemsToSync = Object.values(projectsProgress).reduce(
          (accProject, project) => accProject + Object.values(project).reduce((accTask, task) => accTask + task.totalItemsToSync, 0),
          0,
        );
        const syncedItems = Object.values(projectsProgress).reduce(
          (accProject, project) => accProject + Object.values(project).reduce((accTask, task) => accTask + task.syncedItems, 0),
          0,
        );
        return Math.ceil((syncedItems / totalItemsToSync) * 100);
      }),
    );
    this.isCurrentlySyncing$ = this.currentSyncProgress$.pipe(map(projectsProgress => Object.keys(projectsProgress).length > 0));
    this.lastSyncResult$ = this.#lastSyncResult.asObservable();
  }

  public getProjectCurrentSyncProgress(projectId: string) {
    return this.currentSyncProgress$.pipe(
      map(projectsProgress => {
        if (projectsProgress[projectId]) {
          const project = projectsProgress[projectId];
          const totalItemsToSync = Object.values(project).reduce((accTask, task) => accTask + task.totalItemsToSync, 0);
          const syncedItems = Object.values(project).reduce((accTask, task) => accTask + task.syncedItems, 0);
          return Math.ceil((syncedItems / totalItemsToSync) * 100);
        }
        return 0;
      }),
    );
  }

  public getTaskCurrentSyncProgress(taskId: string) {
    return this.currentSyncProgress$.pipe(
      map(projectsProgress => {
        for (const projectId of Object.keys(projectsProgress)) {
          if (projectsProgress[projectId][taskId]) {
            const taskProgress = projectsProgress[projectId][taskId];
            return Math.ceil((taskProgress.syncedItems / taskProgress.totalItemsToSync) * 100);
          }
        }
        return 0;
      }),
    );
  }

  public isProjectCurrentlySyncing(projectId: string) {
    return this.currentSyncProgress$.pipe(
      map(projectsProgress => {
        if (!projectsProgress[projectId]) {
          return false;
        }
        const projectProgress = projectsProgress[projectId];
        const projectTasksProgress = Object.values(projectProgress);
        const totalItemsToSync = projectTasksProgress.reduce((total, taskProgress) => total + taskProgress.totalItemsToSync, 0);
        const syncedItems = projectTasksProgress.reduce((total, taskProgress) => total + taskProgress.syncedItems, 0);

        return syncedItems < totalItemsToSync;
      }),
    );
  }

  public isTaskCurrentlySyncing(projectId: string, taskId: string) {
    return this.currentSyncProgress$.pipe(
      map(projectsProgress => {
        if (projectsProgress[projectId]?.[taskId]) {
          const taskProgress = projectsProgress[projectId][taskId];
          return taskProgress.syncedItems < taskProgress.totalItemsToSync;
        }
        return false;
      }),
    );
  }

  public async startSync(): Promise<SyncResult> {
    if (await firstValueFrom(this.isCurrentlySyncing$)) {
      this.#logger.warn('Sync already in progress.');
      return { status: 'InProgress' };
    }

    const tasksToSync = await this.getTasksToSync();
    if (tasksToSync.length === 0) {
      this.#logger.warn('No tasks to sync.');
      return { status: 'NoTasksToSync' };
    }

    this.#lastSyncResult.next({ status: 'NoSyncYet' });
    this.#logger.info(`Start sync of ${tasksToSync.length} tasks.`);
    const projectsToSync: SyncProgress = {};
    for (const taskToSync of tasksToSync) {
      if (!projectsToSync[taskToSync.projectId]) {
        projectsToSync[taskToSync.projectId] = {};
      }
      projectsToSync[taskToSync.projectId][taskToSync.taskId] = {
        totalItemsToSync: taskToSync.actionsCount,
        syncedItems: 0,
      };
    }
    this.#currentSyncProgressSubject.next(projectsToSync);

    const taskSyncResults = (await this.syncTasksWithConcurrency(tasksToSync, 2)).taskSyncResults;

    this.#logger.info(`Sync completed`);

    const signedTaskIds = await firstValueFrom(
      this.#downloadedTasksService.signedDownloadedTasks$.pipe(map(tasks => tasks.map(task => task.id))),
    );
    await this.cleanUp(taskSyncResults, signedTaskIds);
    this.setTaskStatuses(taskSyncResults, signedTaskIds);

    this.#currentSyncProgressSubject.next({});

    const status = taskSyncResults.hasFailures() ? 'CompletedWithErrors' : 'Completed';
    this.#logger.info(taskSyncResults.hasFailures() ? `Sync completed with errors` : `Sync completed successfully`);
    this.#lastSyncResult.next({
      status,
      fullySyncedTasks: taskSyncResults.fullySyncedTasks.map(t => ({ taskId: t.taskId, projectId: t.projectId })),
    });
    return { status, taskSyncResults };
  }

  private isTaskFullySynced(projectId: string, taskId: string) {
    return this.isTaskCurrentlySyncing(projectId, taskId).pipe(map(isSyncing => !isSyncing));
  }

  private async getTasksToSync() {
    return await firstValueFrom(this.#queueService.tasksWithActions$);
  }

  private executeWithConcurrencyLimit<R>(limit: number, getNextTaskToExecute: () => (() => Promise<R>) | null): Promise<void> {
    let activeCount = 0;

    return new Promise(resolve => {
      const executeNext = () => {
        while (activeCount < limit) {
          const nextTaskToExecute = getNextTaskToExecute();

          if (!nextTaskToExecute) {
            if (activeCount === 0) {
              resolve();
            }
            return;
          }

          activeCount++;

          nextTaskToExecute().finally(() => {
            activeCount--;
            executeNext();
          });
        }
      };

      executeNext();
    });
  }

  // Sync concurrently because loading all tasks in memory at once can lead to OOM issues, especially task with images, files & HTML reports
  private async syncTasksWithConcurrency(tasksToSync: TaskToSync[], limit: number): Promise<{ taskSyncResults: TaskSyncResults }> {
    const currentlySyncingTasks = new Set<TaskId>();
    const failedSyncs = new Map<TaskId, SyncTaskBundleError>();
    const getNextAvailableBundledTaskToSync = (): BundledTaskToSync | null => {
      for (const taskToSync of tasksToSync) {
        const { taskId, projectId } = taskToSync;
        if (failedSyncs.has(taskId) || currentlySyncingTasks.has(taskId)) {
          continue;
        }

        const syncProgress = this.#currentSyncProgressSubject.getValue()[projectId][taskId];
        if (syncProgress.syncedItems < syncProgress.totalItemsToSync) {
          return { ...taskToSync, bundleIndex: Math.floor(syncProgress.syncedItems / this.#bundleSize) };
        }
      }
      return null;
    };

    const getNextSyncTask = () => {
      const bundledTaskToSync = getNextAvailableBundledTaskToSync();
      if (!bundledTaskToSync) {
        return null;
      }
      const { taskId } = bundledTaskToSync;
      currentlySyncingTasks.add(taskId);
      return async () =>
        await this.syncTaskBundle(bundledTaskToSync)
          .then(syncTaskBundleError => {
            if (syncTaskBundleError) {
              failedSyncs.set(taskId, syncTaskBundleError);
            }
          })
          .finally(() => {
            currentlySyncingTasks.delete(taskId);
          });
    };

    await this.executeWithConcurrencyLimit<SyncTaskBundleError | void>(limit, getNextSyncTask);

    return {
      taskSyncResults: new TaskSyncResults(
        tasksToSync.map(taskToSync => {
          if (!failedSyncs.has(taskToSync.taskId)) {
            return { ...taskToSync, result: 'FullySynced' };
          }
          const error = failedSyncs.get(taskToSync.taskId) as Error;
          const currentTaskSyncProgress = this.#currentSyncProgressSubject.getValue()[taskToSync.projectId][taskToSync.taskId];
          if (currentTaskSyncProgress.syncedItems > 0) {
            return {
              ...taskToSync,
              result: 'PartiallySynced',
              syncedItemsCount: currentTaskSyncProgress.syncedItems,
              error,
            };
          }
          return {
            ...taskToSync,
            result: 'NotSynced',
            error,
          };
        }),
      ),
    };
  }

  private async syncTaskBundle(bundledTaskToSync: BundledTaskToSync): Promise<SyncTaskBundleError | void> {
    const { taskId, projectId, bundleIndex } = bundledTaskToSync;
    this.#logger.info(`Syncing task ${taskId} bundle ${bundleIndex}`);
    this.#tasksStatusService.setTasksAreSyncing([taskId]);

    const actions = await this.#queueService.getTaskActions({ taskId, bundleIndex, bundleSize: this.#bundleSize });
    const payloads = await Promise.all(
      actions.map(async (action, sequence) => await this.createSyncActionPayload(action, sequence + bundleIndex * this.#bundleSize)),
    );
    const syncRequestPayload = this.createSyncRequestPayload(payloads, taskId);

    try {
      await firstValueFrom(
        this.#httpClient.post(this.syncUrl(), syncRequestPayload, {
          withCredentials: true,
          responseType: 'text',
          headers: new HttpHeaders({
            [DEVICE_ID_HEADER]: this.#deviceService.getDeviceId(),
          }),
        }),
      );
      const currentSyncProgress = this.#currentSyncProgressSubject.getValue();
      currentSyncProgress[projectId][taskId].syncedItems += payloads.length;
      this.#currentSyncProgressSubject.next(currentSyncProgress);

      if (await firstValueFrom(this.isTaskFullySynced(projectId, taskId))) {
        this.#syncBackupService.removeRequestBackup(taskId, syncRequestPayload);
        this.#logger.info(`Task ${taskId} sync completed`);
      } else {
        this.#logger.info(`Task ${taskId} bundle ${bundleIndex} sync completed`);
      }
    } catch (err) {
      this.#syncBackupService.backup(taskId, syncRequestPayload);
      this.#logger.error(`Package upload failed for task ${taskId} bundle ${bundleIndex}`, err);
      return err as Error;
    }
  }

  private async createSyncActionPayload(action: Action, sequence: number): Promise<SyncActionPayload> {
    const request: SyncActionPayload = {
      id: action.actionId,
      sequence,
      actionType: action.actionType,
      data: action.request ?? '',
      version: action.version,
      appVersion: action.appVersion,
      platform: action.platform,
    };

    try {
      if (action.actionType === 'CreateTaskFile') {
        const parsedRequest = JSON.parse(action.request ?? '') as CreateFileRequest;
        const syncRequest: SyncCreateTaskFile = {
          id: action.fileId || '',
          description: parsedRequest.description,
          fileName: parsedRequest.fileName,
          fileType: parsedRequest.fileType,
          createdOnUtc: new Date(parsedRequest.createdOnUtc).toISOString(),
        };
        request.data = JSON.stringify(syncRequest);
        const file = await this.#nativeFileService.readTaskFile(action.taskId as string, parsedRequest.fileName);
        if (file) {
          const imageBlob = this.#nativeFileService.base64ToBlob(file.data as string, 'image/jpeg');
          request.file = new File([imageBlob], parsedRequest.fileName);
        }
      }
      if (action.actionType === 'RetakePhotoTaskFile') {
        const parsedRequest = JSON.parse(action.request ?? '') as UpdateTaskFileRequest;
        const syncRequest: SyncUpdateTaskFile = {
          id: action.fileId || '',
          description: parsedRequest.description,
          fileName: parsedRequest.fileName,
          fileType: parsedRequest.fileType,
        };
        request.data = JSON.stringify(syncRequest);
        const file = await this.#nativeFileService.readTaskFile(action.taskId as string, parsedRequest.fileName);
        if (file) {
          const imageBlob = this.#nativeFileService.base64ToBlob(file.data as string, 'image/jpeg');
          request.file = new File([imageBlob], parsedRequest.fileName);
        }
      }

      if (action.actionType === 'AddTaskHTMLReport') {
        const parsedRequest = JSON.parse(action.request ?? '') as AddHtmlReportRequest;
        const parsers = new DOMParser();
        const reportHtmlFileName = `${action.taskId}-${parsedRequest.fileName}`;
        const reportHtml = await this.#nativeFileService.readTaskFile(action.taskId as string, reportHtmlFileName, Encoding.UTF8);
        const html = parsers.parseFromString(reportHtml?.data as string, 'text/html');
        const body = html.body;
        const imageElements: HTMLImageElement[] = [];
        body.querySelectorAll('img').forEach(img => imageElements.push(img));
        for (const img of imageElements) {
          const altText = img.getAttribute('alt');
          const metaLocalOfflineUri = img.getAttribute('data-meta');
          if (metaLocalOfflineUri) {
            if (altText === 'static image') {
              const file = await firstValueFrom(
                this.#databaseService.findOneWithCondition(ReportTemplateImages, { offlineUriPath: metaLocalOfflineUri }),
              );
              if (file?.onlineUriPath) {
                img.setAttribute('src', file.onlineUriPath);
              }
            } else {
              img.setAttribute('src', metaLocalOfflineUri);
            }
          }
          const backgroundOfflineId = img.getAttribute('back-data-meta');
          if (backgroundOfflineId) {
            img.style.backgroundImage = img.style.backgroundImage.replace(/url\(.+\)/, `url(${backgroundOfflineId})`);
          }
        }
        const updatedHTMLString = new XMLSerializer().serializeToString(html);

        request.data = JSON.stringify(parsedRequest);
        request.file = new File([new TextEncoder().encode(updatedHTMLString)], parsedRequest.fileName, { type: 'text/html' });
      }

      if (action.actionType === 'UpdateTaskReport') {
        const { template, ...parsedRequestWithoutTemplate } = JSON.parse(action.request ?? '') as UpdateReportRequest;

        request.data = JSON.stringify(parsedRequestWithoutTemplate);
        request.file = new File([new TextEncoder().encode(template)], parsedRequestWithoutTemplate.fileName, { type: 'text/json' });
      }

      if (action.actionType === 'UpdateCorrosionMonitoringLocation') {
        request.data = JSON.stringify(convertToCorrosionMonitoringLocation(JSON.parse(action.request ?? ''), action.fileId as string));
      }
    } catch (e) {
      this.#logger.error(`Failed to convert pending ${action.actionId} of task ${action.taskId} to a sync request`, e);
    }

    return request;
  }

  private createSyncRequestPayload(syncActionPayloads: SyncActionPayload[], taskId: string) {
    const formData = new FormData();
    syncActionPayloads.forEach(request => {
      if (request.file) {
        formData.append(request.id, request.file);
      }
    });

    const body = JSON.stringify({
      taskId,
      syncUserActions: syncActionPayloads.map(a => ({ ...a, file: a.file && { id: a.id, name: a.file.name, type: a.file.type } })),
    });
    formData.append('body', body);

    return formData;
  }

  private async cleanUp(taskSyncResults: TaskSyncResults, signedTaskIds: string[]): Promise<void> {
    const fullySyncedSignedTasks = taskSyncResults.fullySyncedTasks.filter(result => signedTaskIds.includes(result.taskId));
    await this.removeFullySyncedSignedTasks(fullySyncedSignedTasks);

    const fullySyncedNotSignedTasks = taskSyncResults.fullySyncedTasks.filter(result => !signedTaskIds.includes(result.taskId));
    await this.removeSyncItemsOfFullySyncedNotSignedTasks(fullySyncedNotSignedTasks);

    await this.removeSyncedItemsOfPartiallySyncedTasks(taskSyncResults.partiallySyncedTasks);

    this.#syncBackupService.cleanupExpiredSupportPackages({ excludedTaskIds: taskSyncResults.failedTasks.map(({ taskId }) => taskId) });
  }

  private async removeSyncItemsOfFullySyncedNotSignedTasks(fullySyncedNotSignedTasks: FullySyncedTask[]) {
    for (const { taskId } of fullySyncedNotSignedTasks) {
      await this.#queueService.clearTaskActionsQueue(taskId);
    }
  }

  private async removeFullySyncedSignedTasks(fullySyncedSignedTasks: FullySyncedTask[]) {
    await firstValueFrom(this.#downloadedTasksService.removeDownloadedTasks(fullySyncedSignedTasks.map(task => task.taskId)));
  }

  private async removeSyncedItemsOfPartiallySyncedTasks(partiallySyncedTasks: PartiallySyncedTask[]) {
    for (const { taskId, syncedItemsCount } of partiallySyncedTasks) {
      await this.#queueService.removeTaskActions(taskId, syncedItemsCount);
    }
  }

  private setTaskStatuses(taskSyncResults: TaskSyncResults, signedTaskIds: string[]) {
    const failedTasks = taskSyncResults.failedTasks;
    if (failedTasks.length) {
      this.#tasksStatusService.setTasksSyncHaveFailed(failedTasks.map(({ taskId }) => taskId));
    }

    const fullySyncedNotSignedTasks = taskSyncResults.fullySyncedTasks.filter(result => !signedTaskIds.includes(result.taskId));

    if (fullySyncedNotSignedTasks.length > 0) {
      this.#tasksStatusService.setNotSignedTasksSyncIsDone(fullySyncedNotSignedTasks.map(({ taskId }) => taskId));
    }
  }

  private syncUrl(): string {
    return this.#urlService.getUrl('tasks/sync');
  }
}
